import Cgl from 'Cgl';

import { isOverlapping, isOverlappingColumn, isOverlappingRow } from '../utils';

let _id = 0;

/**
 * @private
 * @author chen yu
 * @date 07/01/2021 01:01:41
 * @class
 */
class BaseConfig {

    constructor({ parent, root, PositionInfo }) {

        this.ID = ++_id;

        /** 
         * @type {LayoutConfig}
         * @description 根节点 */
        this.Root = root || this;
        /**
         * @type {ContainerConfig}
         * @description 父节点
        */
        this.Parent = parent;
        /**
         * @type {Object}
         * @description 布局中的位置信息
         */
        this.PositionInfo = PositionInfo;
        /**
         * @type {VueComponent}
         * @description Vue组件
         */
        this.VueComponent = null;

    }

    /**
     * @author chen yu
     * @date 21/01/2021 20:01:21
     * @memberof BaseConfig
     * @returns {object}
     * @description 导出
     */
    Export() {
        return {
            PositionInfo: Cgl.Object.Clone(this.PositionInfo, true)
        };
    }

    /**
     * @author chen yu
     * @date 12/01/2021 23:01:38
     * @returns {Boundayr} 
     * @memberof BaseConfig
     * @description 获取大小
     */
    GetBoundary() {

        const _boundary = this.VueComponent?.$el?.getBoundingClientRect();

        if (this.Root === this) {
            if (!_boundary) {
                return null;
            }

            return {
                Left: 0,
                Top: 0,
                Right: _boundary.width,
                Bottom: _boundary.height,
                Width: _boundary.width,
                Height: _boundary.height
            };
        } else {

            if (!_boundary) {
                return null;
            }

            const _rootBoundary = this.Root.VueComponent.$el.getBoundingClientRect();

            if (!_rootBoundary) {
                return null;
            }

            return {
                Left: _boundary.left - _rootBoundary.left,
                Right: _boundary.right - _rootBoundary.left,
                Top: _boundary.top - _rootBoundary.top,
                Bottom: _boundary.bottom - _rootBoundary.top,
                Width: _boundary.width,
                Height: _boundary.height
            };

        }

    }

    GetAbsolutePositionInfo() {

        const _positionInfo = this.GetPositionInfo();

        let _row = _positionInfo.Row, _column = _positionInfo.Column;

        let _parent = this.Parent;

        while (_parent) {

            let _parentPositionInfo = _parent.GetPositionInfo();

            _row = _row + _parentPositionInfo.Row - 1;
            _column = _column + _parentPositionInfo.Column - 1;

            _parent = _parent.Parent;

        }

        return {
            ..._positionInfo,
            Row: _row,
            Column: _column
        };

    }

    /**
     * @method
     * @returns {void}
     * @description 移除节点自身
     */
    Remove() {

        if (this.Parent) {

            this.Parent.RemoveChild(this);

        }

    }

    /**
     * @method
     * @returns {object}
     * @description 获取位置信息
     */
    GetPositionInfo() {

        return this.PositionInfo;

    }

    /**
     * @member
     * @returns {object}
     * @description 获取布局信息
     */
    GetLayoutInfo() {
        throw new Error("改方法还未实现。");
    }

    HasOverlap(positionInfo) {

        const _position = Object.assign({}, this.GetPositionInfo(), positionInfo);

        if (positionInfo.Row < 1 || positionInfo.Column < 1) {
            return true;
        }

        if (this.Parent) {

            const _parentPositionInfo = this.Parent.GetPositionInfo();

            if (
                this.Parent.Children.filter(child => child !== this)
                    .some(child =>
                        isOverlappingColumn(_position, child.GetPositionInfo())
                        && isOverlappingRow(_position, child.GetPositionInfo())
                    )
            ) {
                return true;
            }

            if ((positionInfo.Column + positionInfo.Columns - 1) > _parentPositionInfo.Columns) {
                return true;
            }

            if ((positionInfo.Row + positionInfo.Rows - 1) > _parentPositionInfo.Rows) {
                return true;
            }

        }

        return false;

    }

    SetPositionInfo(positionInfo, isAbsolute = true) {

        const _positionInfo = Object.assign({}, isAbsolute ? this.GetAbsolutePositionInfo() : this.GetPositionInfo(), positionInfo);

        if (this.Parent) {

            const _parentAbsolutePositionInfo = this.Parent.GetAbsolutePositionInfo();

            if (_positionInfo.Row < 1) {
                _positionInfo.Row = 1;
            }
    
            if (_positionInfo.Column < 1) {
                _positionInfo.Column = 1;
            }

            if (_positionInfo.Rows < 1) {
                _positionInfo.Rows = 1;
            }

            if (_positionInfo.Columns < 1) {
                _positionInfo.Columns = 1;
            }

            if (_positionInfo.Columns > _parentAbsolutePositionInfo.Columns) {
                _positionInfo.Columns = _parentAbsolutePositionInfo.Columns;
            }

            if (_positionInfo.Rows > _parentAbsolutePositionInfo.Rows) {
                _positionInfo.Rows = _parentAbsolutePositionInfo.Rows;
            }

            const _endColumn = _positionInfo.Column + _positionInfo.Columns;

            if ((_endColumn - 1) > _parentAbsolutePositionInfo.Columns) {

                _positionInfo.Column -= (_endColumn - _parentAbsolutePositionInfo.Columns - 1);

            }

            const _endRow = _positionInfo.Row + _positionInfo.Rows;

            if ((_endRow - 1) > _parentAbsolutePositionInfo.Rows) {

                _positionInfo.Row -= (_endRow - _parentAbsolutePositionInfo.Rows - 1);

            }

            if (!this.HasOverlap(_positionInfo)) {

                this.PositionInfo = {
                    ..._positionInfo,
                    Row: _positionInfo.Row - _parentAbsolutePositionInfo.Row + 1,
                    Column: _positionInfo.Column - _parentAbsolutePositionInfo.Column + 1
                };

            }

        }

        // if (this.Parent && !this.HasOverlap(_position)) {

        //     const _parentAbsolutePositionInfo = this.Parent.GetAbsolutePositionInfo();

        //     this.PositionInfo = {
        //         ..._position,
        //         Row: _position.Row - _parentAbsolutePositionInfo.Row + 1,
        //         Column: _position.Column - _parentAbsolutePositionInfo.Column + 1
        //     };

        // }

        return false;

    }

}

export default BaseConfig;